// @flow
import { MathOperations } from '@phosphor-icons/react'
import * as Sentry from '@sentry/react'
import { Button } from 'antd'
import React from 'react'
import { type Question } from 'types/question'
import { QuestionTitle } from 'ui/Text'

import { useAddNewCalculateFormulaResult } from '@/queries/calculations/useAddNewCalculateFormulaResult'
import Tooltip from '@/ui/atoms/Tooltip'
import FormAntd from '@/ui/form/antd/FormAntd'
import { formatter } from '@/utils/formatter'

import ZentakeFormContext from '../ZentakeFormContext'

type Props = {
  question: Question,
  isOfficeUse: boolean,
}
const CalculationQuestion = ({ question, isOfficeUse }: Props): React$Node => {
  const { answers, onAnswerChanged, getAnswer, templateId } =
    React.useContext(ZentakeFormContext)

  const { addNewCalculateFormulaResult, isLoading } =
    useAddNewCalculateFormulaResult()

  const answer = React.useMemo(() => getAnswer(question), [getAnswer, question])

  const calculateFormulaResult = React.useCallback(async () => {
    try {
      if (isNaN(question?.id)) {
        throw new Error('Question not calculated yet')
      }
      const filteredAnswer = answers?.filter((answer) => !answer.willRemove)

      const { result } = await addNewCalculateFormulaResult?.(
        question.id || '',
        filteredAnswer,
        templateId
      )

      onAnswerChanged(question, result)
    } catch (e) {
      Sentry.captureException(e, {
        extra: { question, answers, templateId },
      })
    }
  }, [
    addNewCalculateFormulaResult,
    answers,
    onAnswerChanged,
    question,
    templateId,
  ])

  let inputValue = answer?.answer_text || ''
  if (question.calculation_value_type === 'percentage' && answer?.answer_text) {
    inputValue = formatter.percent(answer?.answer_text)
  }
  if (question.calculation_value_type === 'currency' && answer?.answer_text) {
    inputValue = formatter.currency(answer?.answer_text)
  }

  let placeholder = '0'
  if (question.calculation_value_type === 'percentage') {
    placeholder = '0.00%'
  }
  if (question.calculation_value_type === 'currency') {
    placeholder = '$0.00'
  }

  return (
    <div>
      <QuestionTitle
        isOfficeUse={isOfficeUse}
        isRequired={question.is_required}
      >
        {question.text}
      </QuestionTitle>
      <div className="relative w-full">
        <FormAntd.Input
          className="transition duration-150 hover:border-gray-200 focus:!border-primary focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)] focus:outline-none focus-visible:border-blue-200 active:border-blue-200"
          disabled={isLoading}
          placeholder={placeholder}
          value={inputValue}
        />
        <Tooltip mouseEnterDelay={0.35} title="Click to calculate">
          <Button
            className="absolute right-0 top-0 h-[38px] rounded-md rounded-l-none px-2 disabled:border-primary/90 disabled:bg-primary/90 disabled:text-white"
            disabled={isLoading}
            loading={isLoading}
            onClick={calculateFormulaResult}
            type="primary"
          >
            <MathOperations className="text-3xl" />
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}

export default CalculationQuestion
